import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertTriangle, Plus, Pencil, Trash2, RefreshCw, Copy, X, KeyRound, Globe } from 'lucide-react';
import api from '../../api';

const inputClass = "px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10";
const primaryBtn = "bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed";
const secondaryBtn = "bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400";
const dangerBtn = "bg-red-500 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-red-600";

const GRANT_TYPES = ['authorization_code', 'refresh_token', 'client_credentials'];

// События, которые сервис авторизации рассылает подписчикам. Пустой список у подписчика
// значит «все»: пропущенное событие хуже лишнего.
const WEBHOOK_EVENTS = ['session.revoked'];

let webhookKeySeq = 0;
const newWebhook = () => ({ id: null, _key: `new-${++webhookKeySeq}`, name: '', url: '', secret: '', events: '', enabled: true });

const emptyForm = {
    clientId: '',
    title: '',
    authTemplate: 'standard',
    publicClient: false,
    grantTypes: ['authorization_code', 'refresh_token'],
    redirectUris: '',
    scopes: '',
    requireConsent: false,
    consentScreen: { title: 'Подтверждение авторизации', description: 'Ознакомьтесь с условиями сервиса и подтвердите передачу данных аккаунта.', documents: [] },
    requirePkce: false,
    accessTokenTtlMinutes: 15,
    webhooks: [],
    tagId: '',
    refreshTokenTtlHours: 168,
    // В форме держим ротацию, а не reuse: галочка должна включать поведение, а не отключать
    // его. На бэкенд уходит обратное значение.
    rotateRefreshTokens: false,
};

// Сроки refresh-токена измеряются днями, и «168 ч» читается хуже, чем «7 дн».
const formatRefreshTtl = (hours) => (hours >= 48 ? `${Math.round(hours / 24)} дн` : `${hours} ч`);

const AdminClientsPage = () => {
    const [clients, setClients] = useState([]);
    const [alert, setAlert] = useState(null);
    const [form, setForm] = useState(null);      // null = форма закрыта
    const [editingId, setEditingId] = useState(null); // null = создание
    const [secret, setSecret] = useState(null);  // { clientId, clientSecret } — показ секрета один раз
    const [copied, setCopied] = useState(false);
    const [tags, setTags] = useState([]);
    const [agreementDocuments, setAgreementDocuments] = useState([]);

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000);
    };

    const errorText = (err, fallback) =>
        err?.response?.data?.detail || (typeof err?.response?.data === 'string' && err.response.data) || fallback;

    const fetchClients = useCallback(() => {
        api.get('/api/admin/clients')
            .then(res => setClients(res.data))
            .catch(() => showAlert('error', 'Не удалось загрузить клиентов'));
    }, []);

    useEffect(() => { fetchClients(); }, [fetchClients]);

    const fetchTags = useCallback(() => {
        api.get('/api/admin/tags')
            .then(res => setTags(res.data))
            .catch(() => setTags([]));
    }, []);

    useEffect(() => { fetchTags(); }, [fetchTags]);
    useEffect(() => {
        api.get('/api/admin/agreements').then(({ data }) => setAgreementDocuments(data))
            .catch(() => showAlert('error', 'Не удалось загрузить реестр соглашений'));
    }, []);


    const openCreate = () => {
        setEditingId(null);
        setForm({ ...emptyForm });
    };

    const openEdit = (client) => {
        setEditingId(client.id);
        setForm({
            clientId: client.clientId,
            title: client.title || '',
            authTemplate: client.authTemplate || 'standard',
            publicClient: client.publicClient,
            grantTypes: client.grantTypes,
            redirectUris: client.redirectUris.join('\n'),
            scopes: client.scopes.join(' '),
            requireConsent: client.requireConsent,
            consentScreen: client.consentScreen || { ...emptyForm.consentScreen, documents: [] },
            requirePkce: client.requirePkce,
            accessTokenTtlMinutes: client.accessTokenTtlMinutes,
            refreshTokenTtlHours: client.refreshTokenTtlHours,
            rotateRefreshTokens: !client.reuseRefreshTokens,
            webhooks: (client.webhooks || []).map(w => ({ ...w, events: (w.events || []).join(', ') })),
            tagId: client.tagId ?? '',
        });
    };

    const buildPayload = () => ({
        clientId: form.clientId.trim(),
        title: form.title.trim(),
        authTemplate: form.authTemplate,
        publicClient: form.publicClient,
        grantTypes: form.grantTypes,
        redirectUris: form.redirectUris.split('\n').map(s => s.trim()).filter(Boolean),
        scopes: form.scopes.split(/[\s,]+/).map(s => s.trim()).filter(Boolean),
        requireConsent: form.requireConsent,
        consentScreen: form.consentScreen,
        requirePkce: form.requirePkce,
        accessTokenTtlMinutes: Number(form.accessTokenTtlMinutes) || 15,
        refreshTokenTtlHours: Number(form.refreshTokenTtlHours) || 168,
        reuseRefreshTokens: !form.rotateRefreshTokens,
        // Пусто - клиент меток не выдаёт.
        tagId: form.tagId === '' ? null : Number(form.tagId),
        webhooks: form.webhooks.map(w => ({
            id: w.id ?? null,
            name: (w.name || '').trim(),
            url: (w.url || '').trim(),
            // Пустой ключ означает «оставить прежний», а у нового подписчика — «сгенерируй сам»
            secret: (w.secret || '').trim() || null,
            events: (w.events || '').split(/[\s,]+/).map(e => e.trim()).filter(Boolean),
            enabled: w.enabled,
        })),
    });

    const handleSubmit = () => {
        const payload = buildPayload();
        if (!payload.clientId) {
            showAlert('error', 'Client ID обязателен');
            return;
        }
        if (!payload.title) {
            showAlert('error', 'Укажите название сервиса (Title)');
            return;
        }
        if (!payload.consentScreen.title.trim()) {
            showAlert('error', 'Укажите заголовок окна согласия');
            return;
        }
        for (const document of payload.consentScreen.documents) {
            if (!document.documentId) {
                showAlert('error', 'Выберите документы из единого реестра соглашений');
                return;
            }
        }
        const request = editingId
            ? api.put(`/api/admin/clients/${editingId}`, payload)
            : api.post('/api/admin/clients', payload);

        request
            .then(res => {
                setForm(null);
                fetchClients();
                if (!editingId && res.data.clientSecret) {
                    setSecret({ title: payload.title, clientId: res.data.clientId, clientSecret: res.data.clientSecret });
                } else {
                    showAlert('success', editingId ? 'Клиент обновлен' : 'Клиент создан');
                }
            })
            .catch(err => showAlert('error', errorText(err, 'Не удалось сохранить клиента')));
    };

    const handleDelete = (client) => {
        if (!window.confirm(`Удалить клиента «${client.title || 'Без названия'}»? Приложения, использующие его, перестанут работать.`)) return;
        api.delete(`/api/admin/clients/${client.id}`)
            .then(() => {
                showAlert('success', 'Клиент удален');
                fetchClients();
            })
            .catch(err => showAlert('error', errorText(err, 'Не удалось удалить клиента')));
    };

    const handleRegenerate = (client) => {
        if (!window.confirm(`Перевыпустить секрет для «${client.title || 'Без названия'}»? Старый секрет перестанет работать.`)) return;
        api.post(`/api/admin/clients/${client.id}/regenerate-secret`)
            .then(res => setSecret({ title: client.title, clientId: res.data.clientId, clientSecret: res.data.clientSecret }))
            .catch(err => showAlert('error', errorText(err, 'Не удалось перевыпустить секрет')));
    };

    const copySecret = () => {
        navigator.clipboard.writeText(secret.clientSecret).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const updateWebhook = (index, patch) => {
        setForm(f => ({ ...f, webhooks: f.webhooks.map((w, i) => (i === index ? { ...w, ...patch } : w)) }));
    };

    const addWebhook = () => setForm(f => ({ ...f, webhooks: [...f.webhooks, newWebhook()] }));

    const removeWebhook = (index) => setForm(f => ({ ...f, webhooks: f.webhooks.filter((_, i) => i !== index) }));

    const toggleGrant = (gt) => {
        setForm(f => ({
            ...f,
            grantTypes: f.grantTypes.includes(gt) ? f.grantTypes.filter(g => g !== gt) : [...f.grantTypes, gt]
        }));
    };

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <h1 className="text-[28px] font-semibold text-gray-900 m-0">OAuth2-клиенты</h1>
                <button className={primaryBtn + ' flex items-center gap-2'} onClick={openCreate}>
                    <Plus size={16} /> Создать клиента
                </button>
            </div>

            {alert && (
                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${alert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {alert.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    <span>{alert.message}</span>
                </div>
            )}

            {clients.map(client => (
                <div key={client.id} className="bg-white rounded-xl p-6 mb-4 shadow-sm border border-gray-200">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="m-0 text-lg font-semibold text-gray-900 truncate">{client.title || 'Без названия'}</h3>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${client.publicClient ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                                    {client.publicClient ? 'Публичный (SPA)' : 'Конфиденциальный'}
                                </span>
                            </div>
                            <details className="text-xs text-gray-500 mb-2">
                                <summary className="cursor-pointer">Технический идентификатор</summary>
                                <code className="block mt-1 break-all">{client.clientId}</code>
                            </details>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {client.grantTypes.map(gt => (
                                    <span key={gt} className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{gt}</span>
                                ))}
                                {client.scopes.map(s => (
                                    <span key={s} className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">{s}</span>
                                ))}
                            </div>
                            {client.redirectUris.length > 0 && (
                                <div className="text-xs text-gray-500 flex flex-col gap-0.5">
                                    {client.redirectUris.map(uri => (
                                        <span key={uri} className="flex items-center gap-1.5 truncate"><Globe size={12} className="shrink-0" /> {uri}</span>
                                    ))}
                                </div>
                            )}
                            <div className="text-xs text-gray-400 mt-2">
                                Access-токен: {client.accessTokenTtlMinutes} мин
                                {' · Refresh: '}{formatRefreshTtl(client.refreshTokenTtlHours)}
                                {!client.reuseRefreshTokens && ' · Ротация'}
                                {client.requireConsent && ' · Consent'}
                                {client.requirePkce && ' · PKCE'}
                                {client.webhooks?.length > 0 && ` · подписчиков: ${client.webhooks.length}`}
                                {client.tagName && ` · метка: ${client.tagName}`}
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            {!client.publicClient && (
                                <button className={secondaryBtn + ' flex items-center gap-1.5'} title="Перевыпустить секрет" onClick={() => handleRegenerate(client)}>
                                    <RefreshCw size={14} /> Секрет
                                </button>
                            )}
                            <button className={secondaryBtn + ' flex items-center gap-1.5'} onClick={() => openEdit(client)}>
                                <Pencil size={14} /> Изменить
                            </button>
                            <button className={dangerBtn + ' flex items-center gap-1.5'} onClick={() => handleDelete(client)}>
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>
                </div>
            ))}
            {clients.length === 0 && (
                <div className="bg-white rounded-xl p-10 text-center text-gray-400 shadow-sm border border-gray-200">
                    Клиентов пока нет
                </div>
            )}

            {/*
                Сами метки живут в своём разделе: там же настраивается приветственное письмо
                сервиса, и держать управление ими в двух местах значит однажды разойтись.
                Здесь метка только выбирается - в форме клиента выше.
            */}

            {form && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setForm(null)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="m-0 text-lg font-semibold text-gray-900">{editingId ? 'Редактирование клиента' : 'Новый клиент'}</h3>
                            <button className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 p-1" onClick={() => setForm(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Client ID</label>
                            <input
                                className={editingId ? inputClass + ' bg-gray-50 text-gray-500' : inputClass}
                                type="text"
                                placeholder="my-service-client"
                                value={form.clientId}
                                disabled={!!editingId}
                                onChange={e => setForm({ ...form, clientId: e.target.value })}
                            />
                        </div>
                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label htmlFor="client-title" className="text-sm font-medium text-gray-700">Название сервиса (Title)</label>
                            <input id="client-title" className={inputClass} type="text" required maxLength={200} placeholder="Мой сервис" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                            <p className="text-xs text-gray-500 m-0">Отображается в списке клиентов и на странице подтверждения входа.</p>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Метка сервиса</label>
                            <select
                                className={inputClass}
                                value={form.tagId}
                                onChange={e => setForm({ ...form, tagId: e.target.value })}
                            >
                                <option value="">Без метки</option>
                                {tags.map(tag => (
                                    <option key={tag.id} value={tag.id}>{tag.name}</option>
                                ))}
                            </select>
                            <span className="text-[13px] text-gray-500">
                                Достаётся пользователю при входе в этого клиента и держится год с
                                последнего входа. По меткам фильтруются рассылки.
                            </span>
                        </div>

                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-4">
                            <input type="checkbox" checked={form.publicClient} onChange={e => setForm({ ...form, publicClient: e.target.checked })} />
                            <span>Публичный клиент (SPA, без секрета)</span>
                        </label>

                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Grant types</label>
                            <div className="flex flex-col gap-2">
                                {GRANT_TYPES.map(gt => (
                                    <label key={gt} className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="checkbox" checked={form.grantTypes.includes(gt)} onChange={() => toggleGrant(gt)} />
                                        <span>{gt}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Redirect URI (по одному на строку)</label>
                            <textarea
                                className={inputClass + ' min-h-[80px] font-mono text-sm'}
                                placeholder={'https://app.example.com/login/oauth2/code/auth-server'}
                                value={form.redirectUris}
                                onChange={e => setForm({ ...form, redirectUris: e.target.value })}
                            />
                        </div>
                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Scopes (через пробел)</label>
                            <input className={inputClass} type="text" placeholder="openid profile email" value={form.scopes} onChange={e => setForm({ ...form, scopes: e.target.value })} />
                        </div>
                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Время жизни access-токена (минуты)</label>
                            <input className={inputClass} type="number" min="1" value={form.accessTokenTtlMinutes} onChange={e => setForm({ ...form, accessTokenTtlMinutes: e.target.value })} />
                        </div>
                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Время жизни refresh-токена (часы)</label>
                            <input className={inputClass} type="number" min="1" value={form.refreshTokenTtlHours} onChange={e => setForm({ ...form, refreshTokenTtlHours: e.target.value })} />
                            <span className="text-xs text-gray-500">
                                Должно быть больше времени жизни сессии портала — иначе вход умрёт раньше, чем истечёт сессия.
                            </span>
                        </div>

                        <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer mb-2">
                            <input type="checkbox" className="mt-1" checked={form.rotateRefreshTokens} onChange={e => setForm({ ...form, rotateRefreshTokens: e.target.checked })} />
                            <span>
                                Ротация refresh-токена
                                <span className="block text-xs text-gray-500">
                                    Срок продлевается при каждом обновлении. Для нативных приложений — да,
                                    для браузерных — нет: параллельные запросы гонятся за обновлением.
                                </span>
                            </span>
                        </label>
                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-2">
                            <input type="checkbox" checked={form.requireConsent} onChange={e => setForm({ ...form, requireConsent: e.target.checked })} />
                            <span>Окно подтверждения авторизации и принятия документов</span>
                        </label>
                        <div className="flex flex-col gap-2 mb-5">
                            <label htmlFor="client-auth-template" className="text-sm font-medium text-gray-700">Оформление авторизации</label>
                            <select id="client-auth-template" aria-describedby="client-auth-template-hint" className={inputClass}
                                    value={form.authTemplate} onChange={e => setForm({ ...form, authTemplate: e.target.value })}>
                                <option value="standard">IT-Burger (стандартное)</option>
                                <option value="burgerbot">BurgerBot</option>
                            </select>
                            <span id="client-auth-template-hint" className="text-xs text-gray-500">Применяется ко всем экранам входа и consent этого клиента.</span>
                        </div>
                        {form.requireConsent && (
                            <fieldset className="border border-gray-200 rounded-lg p-4 mb-5 text-left space-y-3">
                                <legend className="text-sm font-medium text-gray-700 px-1">Окно согласия</legend>
                                <p className="text-xs text-gray-500 m-0">Показываются все ещё не принятые документы. Обязательные нужно принять для входа. Неотмеченные необязательные записываются как игнор на шесть месяцев; новая версия предлагается заново.</p>
                                <label className="block text-sm text-gray-700">Заголовок
                                    <input className={inputClass + ' w-full mt-1'} maxLength={200} value={form.consentScreen.title}
                                           onChange={e => setForm({ ...form, consentScreen: { ...form.consentScreen, title: e.target.value } })} />
                                </label>
                                <label className="block text-sm text-gray-700">Пояснение
                                    <textarea className={inputClass + ' w-full mt-1'} rows={3} maxLength={4000} value={form.consentScreen.description}
                                              onChange={e => setForm({ ...form, consentScreen: { ...form.consentScreen, description: e.target.value } })} />
                                </label>
                                {form.consentScreen.documents.map((document, i) => (
                                    <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                                        <label className="block text-sm text-gray-700">Документ {i + 1} из реестра
                                            <select className={inputClass + ' w-full mt-1'} value={document.documentId || ''} onChange={e => {
                                                const chosen = agreementDocuments.find(d => d.document_id === e.target.value);
                                                setForm({ ...form, consentScreen: { ...form.consentScreen, documents: form.consentScreen.documents.map((d, index) => index === i ? { ...d, documentId: chosen?.document_id || '', title: chosen?.accept_title || '', url: chosen?.url || '', version: chosen?.version || null, docHash: chosen?.doc_hash || null } : d) } });
                                            }}>
                                                <option value="">Выберите документ</option>
                                                {agreementDocuments.map(d => <option key={d.document_id} value={d.document_id}>{d.title} · v{d.version}</option>)}
                                            </select>
                                        </label>
                                        <p className="text-xs text-gray-500 break-words">{document.title} · {document.url}</p>
                                        <label className="flex items-center gap-2 text-sm text-gray-700">
                                            <input type="checkbox" checked={document.required !== false}
                                                   onChange={e => setForm({ ...form, consentScreen: { ...form.consentScreen, documents: form.consentScreen.documents.map((d, index) => index === i ? { ...d, required: e.target.checked } : d) } })} />
                                            Обязательное согласие
                                        </label>
                                        <button type="button" className="text-sm text-red-600" onClick={() => setForm({ ...form, consentScreen: { ...form.consentScreen, documents: form.consentScreen.documents.filter((_, index) => index !== i) } })}>Удалить документ</button>
                                    </div>
                                ))}
                                <button type="button" className={secondaryBtn} disabled={!editingId || form.consentScreen.documents.length >= 20}
                                        onClick={() => setForm({ ...form, consentScreen: { ...form.consentScreen, documents: [...form.consentScreen.documents, { documentId: '', title: '', url: '', required: true }] } })}>Добавить документ</button>
                                <p className="text-xs text-gray-500">Сначала разрешите этому OAuth2-клиенту доступ в карточке документа. Названия, ссылки и версии редактируются в разделе «Соглашения».</p>
                                {!editingId && <p className="text-xs text-gray-500">Сначала создайте клиента, затем выдайте ему доступ к документам и добавьте их в окно согласия.</p>}
                            </fieldset>
                        )}
                        <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer mb-5">
                            <input type="checkbox" checked={form.requirePkce} onChange={e => setForm({ ...form, requirePkce: e.target.checked })} />
                            <span>Требовать PKCE</span>
                        </label>

                        <div className="mb-5 text-left">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700">Подписчики на события</label>
                                <button className={secondaryBtn + ' !px-3 !py-1.5 flex items-center gap-1.5'} onClick={addWebhook}>
                                    <Plus size={14} /> Добавить
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">
                                Куда сообщать о событиях этого клиента — например, об отключении устройства.
                                Приёмник проверяет заголовок <span className="font-mono">X-Burger-Signature</span> тем же ключом.
                            </p>

                            {form.webhooks.length === 0 ? (
                                <p className="text-sm text-gray-500 m-0">Подписчиков нет — события никуда не уходят.</p>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {form.webhooks.map((w, i) => (
                                        <div key={w.id ?? w._key} className="border border-gray-200 rounded-lg p-3 flex flex-col gap-2">
                                            <div className="flex gap-2">
                                                <input className={inputClass + ' flex-1'} type="text" placeholder="Имя, например constructor"
                                                       value={w.name} onChange={e => updateWebhook(i, { name: e.target.value })} />
                                                <button
                                                    className="bg-transparent text-red-500 px-3 border border-red-300 rounded-lg cursor-pointer transition-all hover:bg-red-50 hover:border-red-500"
                                                    title="Удалить подписчика"
                                                    onClick={() => removeWebhook(i)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                            <input className={inputClass} type="text" placeholder="https://api.example.com/internal/auth-events"
                                                   value={w.url} onChange={e => updateWebhook(i, { url: e.target.value })} />
                                            <input className={inputClass + ' font-mono text-[13px]'} type="text"
                                                   placeholder="Ключ подписи — оставьте пустым, чтобы сгенерировать"
                                                   value={w.secret} onChange={e => updateWebhook(i, { secret: e.target.value })} />
                                            <input className={inputClass} type="text"
                                                   placeholder={`События через запятую — пусто, чтобы все (${WEBHOOK_EVENTS.join(', ')})`}
                                                   value={w.events} onChange={e => updateWebhook(i, { events: e.target.value })} />
                                            <label className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                                                <input type="checkbox" checked={w.enabled} onChange={e => updateWebhook(i, { enabled: e.target.checked })} />
                                                <span>Включён</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button className={primaryBtn} onClick={handleSubmit}>{editingId ? 'Сохранить' : 'Создать'}</button>
                            <button className={secondaryBtn} onClick={() => setForm(null)}>Отмена</button>
                        </div>
                    </div>
                </div>
            )}

            {secret && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 w-full max-w-[480px] shadow-xl">
                        <div className="flex items-center gap-2.5 mb-4">
                            <KeyRound size={20} className="text-amber-500" />
                            <h3 className="m-0 text-lg font-semibold text-gray-900">Секрет клиента «{secret.title || 'Без названия'}»</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            Скопируйте секрет сейчас — он показывается только один раз и не хранится в открытом виде.
                        </p>
                        <div className="flex items-center gap-2 mb-5">
                            <code className="flex-1 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono break-all">{secret.clientSecret}</code>
                            <button className={secondaryBtn + ' flex items-center gap-1.5 shrink-0'} onClick={copySecret}>
                                {copied ? <CheckCircle size={14} className="text-emerald-600" /> : <Copy size={14} />}
                                {copied ? 'Скопировано' : 'Копировать'}
                            </button>
                        </div>
                        <button className={primaryBtn} onClick={() => { setSecret(null); setCopied(false); }}>Я сохранил секрет</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminClientsPage;
